import { useQuery } from '@tanstack/react-query';
import { twJoin } from 'tailwind-merge';

import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { METRIC_LABEL_SIZE } from '@/constants/badges';
import { getResponseTimeSettingsV2QueryOptions } from '@/services/getResponseTimeSettingsV2';

/**
 * The expert's response-time tier, as a mark and a label with no container.
 *
 * The fill this used to carry was a button shape on a static fact, inside surfaces that
 * are themselves pressable. Dropping it also lets the badge beside it share the row
 * instead of stacking under it, which the filled pair could not do at any directory
 * column width without shortening this label. The profile page already rendered this as a
 * plain icon and label through its info-item list, so the fill was only ever on some
 * surfaces and this makes them agree.
 *
 * The clock icon is gone once the flag is on. It cost the row 28px including its gap, which
 * is what truncated this label at directory column width, and it was buying nothing the copy
 * did not already say: the server-owned label spells out "response time" in words. The tier's
 * flame still arrives inside that label string rather than as an element here, so the fastest
 * tier keeps its mark and the slower tiers open with words like every other row.
 *
 * The label is server-owned copy from the response-time settings endpoint and is passed
 * through untouched, so its wording and its flame are a config decision rather than a
 * component one. That also means prominence is already tiered without this component doing
 * anything: the fastest tier arrives carrying a flame and the slower ones do not, which is
 * the right way round, since the slowest tier is not the thing to advertise.
 *
 * Ink is `typography-950` at normal weight, the same type as the Reference Champion badge it
 * shares a row with. It carried the muted `typography-700` for a long time, which made it the
 * only line in the expert sidebar lighter than the institution, title and focus-area text
 * beside it, so a fact about the expert read as a caption.
 *
 * The badge used to outrank it by a weight step and an ink step. That is gone by choice: two
 * facts on one row set in two different weights read as accidental at least as readily as they
 * read as ranked, and the row is a group rather than a hierarchy. What separates the two now is
 * the badge's trailing mark, and its outline on the surfaces where it is pressable.
 *
 * That leaves the directory card resting on the mark alone, since the badge is not pressable
 * there and carries no outline. The mark is operator-owned and may be omitted, so a payload
 * that sets no emoji makes the two indistinguishable on that surface. It is a config decision
 * away rather than a code change, which is worth knowing before turning the mark off.
 *
 * This rendering is unconditional. It was briefly gated on the reference badge flag, not
 * because anyone wanted the filled pill back, but because the redesign had shipped ungated
 * and the flag was the quickest way to restore production while it was reviewed. Keeping
 * that gate would have meant the badge could never be enabled without also changing this
 * component's seven call sites, so the two decisions could never be made separately.
 */
export const ResponseTime = ({
  responseTime,
  responseTimeLabel,
  fontSize = 'text-xs',
  className,
}: {
  responseTime?: string | null;
  responseTimeLabel?: string;
  fontSize?: 'text-xs' | 'text-sm';
  className?: string;
}) => {
  const { data: responseTimeBuckets = [] } = useQuery(getResponseTimeSettingsV2QueryOptions());

  const matchingBucket = responseTime
    ? responseTimeBuckets.find((bucket) => parseInt(responseTime, 10) <= bucket.maxTime)
    : undefined;

  const label = responseTimeLabel ?? matchingBucket?.label;

  if (!responseTimeLabel && !responseTime) {
    return null;
  }

  return (
    <HStack
      data-component="response-time"
      className={twJoin('min-w-0 items-center gap-2', className)}>
      <Text
        className={twJoin('font-normal', METRIC_LABEL_SIZE[fontSize], 'text-typography-950')}
        numberOfLines={1}>
        {label ?? ''}
      </Text>
    </HStack>
  );
};
